import { AnalyticsChartCard } from '@/components/ui/analytics/AnalyticsChartCard';
import { PeriodFilter } from '@/components/ui/analytics/PeriodFilter';
import { SummaryMetricCard } from '@/components/ui/analytics/SummaryMetricCard';
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '@/components/ui/shadcn/chart';
import { useTranslations } from '@/hooks/useTranslations';
import { categoricalColor, PROJECT_STATUS_COLORS, statusColor } from '@/lib/chart-colors';
import { settingLabel } from '@/lib/status-labels';
import { formatCurrency } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { Bell, Building2, ChevronRight, FolderKanban, Lightbulb, Loader2, TrendingDown, TrendingUp, UserCircle, UserX, Wrench } from 'lucide-react';
import * as React from 'react';
import { Area, AreaChart, CartesianGrid, Cell, Label, Pie, PieChart, XAxis, YAxis } from 'recharts';

interface DashboardProps {
    /** Years that actually have data, for the period filter's year select. */
    availableYears?: number[];
    dateRange: {
        from_date: string;
        to_date: string;
    };
    dashboardData: {
        statistics: {
            totalLeads: number;
            totalProjects: number;
            totalCompanies: number;
            totalWorkOrders: number;
            monthlyRevenue: number;
            monthlyRevenueGrowth: number;
            leadsCreated: number;
            projectsCreated: number;
            workOrdersCreated: number;
        };
        monthlyRevenueSeries: Array<{
            label: string;
            leadsRevenue: number;
            projectsRevenue: number;
        }>;
        leadsProjectsTimeline: Array<{ period: string; leads: number; projects: number }>;
        leadFunnel: Array<{ status: string; count: number; ratio: number }>;
        projectStatus: {
            total: number;
            breakdown: Array<{ status: string; count: number; ratio: number }>;
        };
        recentActivity: Array<{ type: string; title: string; created_at: string | null }>;
        topSalesmen: Array<{
            id: string;
            name: string;
            avatar?: string | null;
            revenue: number;
            projectsCount: number;
        }>;
        topServiceEmployees: Array<{
            id: string;
            name: string;
            avatar?: string | null;
            completedCount: number;
        }>;
        topCompanies: Array<{
            id: string;
            name: string;
            revenue: number;
            projectsCount: number;
        }>;
    };
}

function toSentenceCase(value: string): string {
    const normalized = value.replaceAll('_', ' ').trim().toLowerCase();
    if (normalized.length === 0) {
        return normalized;
    }

    return normalized[0].toUpperCase() + normalized.slice(1);
}

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return '--';
    }

    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function EmptyState({ title }: { title: string }) {
    return (
        <div className="border-border bg-muted text-muted-foreground flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm">
            <UserX className="h-4 w-4" />
            <span>{title}</span>
        </div>
    );
}

function AvatarOrInitials({ name, avatar }: { name: string; avatar?: string | null }) {
    if (avatar) {
        return <img src={avatar} alt={name} className="h-7 w-7 rounded-full object-cover" />;
    }

    return (
        <span className="bg-primary/10 text-primary flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold">
            {getInitials(name)}
        </span>
    );
}

export function Dashboard({ dashboardData, dateRange, availableYears }: DashboardProps) {
    const { t } = useTranslations();
    const { statistics, leadsProjectsTimeline, leadFunnel, projectStatus, topSalesmen, topServiceEmployees, topCompanies } = dashboardData;

    // Pending state for period-filter navigations (mirrors the analytics dashboard).
    const [isNavigating, setIsNavigating] = React.useState(false);

    // For leads/projects/work orders we show "closed / registered" — the
    // outcome count vs how many were created (registered) in the same period.
    const closedOverCreated = (closed: number, created: number) => `${closed.toLocaleString()} / ${created.toLocaleString()}`;

    const statsCards = [
        {
            key: 'leads',
            docKey: 'homepage.leads',
            label: toSentenceCase(t('homepage.dashboard.statistics.leads', 'Leads')),
            subtitle: t('homepage.dashboard.statistics.leads_subtitle', 'Won or converted / registered'),
            value: closedOverCreated(statistics.totalLeads, statistics.leadsCreated),
            icon: UserCircle,
        },
        {
            key: 'projects',
            docKey: 'homepage.projects',
            label: toSentenceCase(t('homepage.dashboard.statistics.projects', 'Projects')),
            subtitle: t('homepage.dashboard.statistics.projects_subtitle', 'Completed / registered in the selected period'),
            value: closedOverCreated(statistics.totalProjects, statistics.projectsCreated),
            icon: FolderKanban,
        },
        {
            key: 'work_orders',
            docKey: 'homepage.work_orders',
            label: toSentenceCase(t('homepage.dashboard.statistics.work_orders', 'Work orders')),
            subtitle: t('homepage.dashboard.statistics.work_orders_subtitle', 'Completed / registered in the selected period'),
            value: closedOverCreated(statistics.totalWorkOrders, statistics.workOrdersCreated),
            icon: Wrench,
        },
        {
            key: 'companies',
            docKey: 'homepage.companies',
            label: toSentenceCase(t('homepage.dashboard.statistics.companies', 'Companies')),
            subtitle: t('homepage.dashboard.statistics.companies_subtitle', 'Added in the selected period'),
            value: statistics.totalCompanies.toLocaleString(),
            icon: Building2,
        },
    ];

    const revenueUp = statistics.monthlyRevenueGrowth >= 0;
    const insights = [
        {
            icon: revenueUp ? TrendingUp : TrendingDown,
            iconColor: revenueUp ? 'text-success-subtle-foreground' : 'text-warning-subtle-foreground',
            title: t('homepage.analytics.insights.revenue_change', 'Revenue change'),
            text: t('homepage.analytics.insights.vs_previous_period', ':percent% vs previous period').replace(
                ':percent',
                statistics.monthlyRevenueGrowth.toString(),
            ),
        },
        {
            icon: TrendingUp,
            iconColor: 'text-success-subtle-foreground',
            title: t('homepage.analytics.insights.best_seller', 'Best seller'),
            text: topSalesmen[0]
                ? t('homepage.analytics.insights.leads_by_revenue', ':name leads by revenue').replace(':name', topSalesmen[0].name)
                : t('homepage.analytics.empty.no_data_in_selected_range', 'No data in selected range'),
        },
        {
            icon: Bell,
            iconColor: 'text-info-subtle-foreground',
            title: t('homepage.analytics.insights.service_queue', 'Service queue'),
            text: t('homepage.analytics.insights.completed_work_orders', ':count completed work orders').replace(
                ':count',
                statistics.totalWorkOrders.toString(),
            ),
        },
    ];

    const projectStatusBarData = projectStatus.breakdown.map((item) => ({
        ...item,
        label: settingLabel(t, 'project_statuses', item.status),
        fill: statusColor(PROJECT_STATUS_COLORS, item.status),
    }));
    const projectSplitConfig = projectStatusBarData.reduce<Record<string, { label: string; color: string }>>((acc, entry) => {
        acc[entry.label] = { label: entry.label, color: entry.fill };
        return acc;
    }, {});

    return (
        <div className="relative space-y-4">
            <div>
                <h1 className="text-foreground text-3xl font-semibold">{toSentenceCase(t('homepage.title', 'Home'))}</h1>
                <p className="text-muted-foreground text-sm">
                    {t('homepage.dashboard.subheading', 'What happened across sales, projects and service in the selected period.')}
                </p>
            </div>

            {/* Filter toolbar — its own row above the cards, matching the analytics page. */}
            <div className="bg-card flex flex-col gap-3 rounded-xl border p-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
                <PeriodFilter dateRange={dateRange} availableYears={availableYears} onNavigatingChange={setIsNavigating} />
            </div>

            <div className={isNavigating ? 'pointer-events-none space-y-4 opacity-50 transition-opacity' : 'space-y-4 transition-opacity'}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {statsCards.map((item) => (
                        <SummaryMetricCard
                            key={item.key}
                            docKey={item.docKey}
                            title={item.label}
                            value={item.value}
                            subtitle={item.subtitle}
                            icon={<item.icon className="h-4 w-4" />}
                        />
                    ))}
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                    <AnalyticsChartCard
                        className="xl:col-span-5"
                        docKey="homepage.leads_projects_trend"
                        title={t('homepage.analytics.leads_projects_trend.title', 'Leads & Projects Trend')}
                        description={t(
                            'homepage.analytics.leads_projects_trend.description',
                            'New leads and started projects over the selected period.',
                        )}
                    >
                        {leadsProjectsTimeline.length === 0 ? (
                            <EmptyState title={t('homepage.analytics.empty.no_data_in_selected_range', 'No data in selected range')} />
                        ) : (
                            <ChartContainer
                                role="img"
                                aria-label={t('homepage.analytics.leads_projects_trend.title', 'Leads & Projects Trend')}
                                config={{
                                    leads: { label: t('homepage.analytics.leads_projects_trend.series_leads', 'New leads'), color: '#2563eb' },
                                    projects: {
                                        label: t('homepage.analytics.leads_projects_trend.series_projects', 'New projects'),
                                        color: '#7c3aed',
                                    },
                                }}
                                className="h-72 w-full"
                            >
                                <AreaChart data={leadsProjectsTimeline}>
                                    <defs>
                                        <linearGradient id="homeFillLeads" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />
                                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0.05} />
                                        </linearGradient>
                                        <linearGradient id="homeFillProjects" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.4} />
                                            <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.05} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                                    <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                                    <YAxis allowDecimals={false} domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                                    <ChartTooltip content={<ChartTooltipContent />} />
                                    <ChartLegend content={<ChartLegendContent />} />
                                    <Area dataKey="leads" type="monotone" stroke="#2563eb" strokeWidth={2} fill="url(#homeFillLeads)" />
                                    <Area dataKey="projects" type="monotone" stroke="#7c3aed" strokeWidth={2} fill="url(#homeFillProjects)" />
                                </AreaChart>
                            </ChartContainer>
                        )}
                    </AnalyticsChartCard>

                    <AnalyticsChartCard
                        className="xl:col-span-3"
                        contentClassName="space-y-3"
                        docKey="homepage.leads_funnel"
                        title={t('homepage.analytics.sales_funnel.title', 'Leads funnel')}
                        description={t('homepage.analytics.sales_funnel.description', 'Leads funnel in the selected period')}
                    >
                        {leadFunnel.length === 0 && (
                            <EmptyState title={t('homepage.analytics.empty.no_lead_data', 'No lead data in selected range')} />
                        )}
                        {leadFunnel.slice(0, 6).map((step, index) => (
                            <div key={step.status} className="space-y-1">
                                <div className="text-muted-foreground flex items-center justify-between text-xs">
                                    <span className="truncate">{settingLabel(t, 'lead_statuses', step.status)}</span>
                                    <span>
                                        {step.count} ({step.ratio}%)
                                    </span>
                                </div>
                                <div className="bg-muted h-3 w-full rounded-full">
                                    <div
                                        className="h-3 rounded-full"
                                        style={{
                                            width: `${Math.max(step.ratio, 3)}%`,
                                            backgroundColor: categoricalColor(index),
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </AnalyticsChartCard>

                    <AnalyticsChartCard
                        className="xl:col-span-4"
                        docKey="homepage.project_split"
                        title={t('homepage.analytics.project_split.title', 'Project split')}
                        description={t(
                            'homepage.analytics.project_split.description',
                            'Projects with a key date (signed, started, due, or completed) in the selected period, grouped by status. Includes projects carried over from earlier periods.',
                        )}
                    >
                        {projectStatus.breakdown.length === 0 ? (
                            <EmptyState title={t('homepage.analytics.empty.no_project_data', 'No project data in selected range')} />
                        ) : (
                            <div className="space-y-4">
                                <ChartContainer
                                    role="img"
                                    aria-label={t('homepage.analytics.project_split.title', 'Project split')}
                                    config={projectSplitConfig}
                                    className="mx-auto aspect-square max-h-[240px]"
                                >
                                    <PieChart>
                                        <ChartTooltip cursor={false} content={<ChartTooltipContent nameKey="label" />} />
                                        <Pie
                                            data={projectStatusBarData}
                                            dataKey="count"
                                            nameKey="label"
                                            innerRadius={60}
                                            outerRadius={95}
                                            paddingAngle={2}
                                            strokeWidth={2}
                                        >
                                            {projectStatusBarData.map((entry) => (
                                                <Cell key={entry.status} fill={entry.fill} />
                                            ))}
                                            <Label
                                                content={({ viewBox }) => {
                                                    if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                                                        const { cx, cy } = viewBox as { cx: number; cy: number };
                                                        return (
                                                            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
                                                                <tspan x={cx} y={cy} className="fill-foreground text-3xl font-bold">
                                                                    {projectStatus.total.toLocaleString()}
                                                                </tspan>
                                                                <tspan x={cx} y={cy + 22} className="fill-muted-foreground text-sm">
                                                                    {t('homepage.analytics.table.projects', 'Projects')}
                                                                </tspan>
                                                            </text>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                        </Pie>
                                    </PieChart>
                                </ChartContainer>

                                {/* Legend / agenda */}
                                <div className="space-y-1.5">
                                    {projectStatusBarData.map((entry) => (
                                        <div key={entry.status} className="flex items-center justify-between text-sm">
                                            <span className="flex items-center gap-2">
                                                <span className="h-2.5 w-2.5 shrink-0 rounded-none" style={{ backgroundColor: entry.fill }} />
                                                <span className="text-muted-foreground">{entry.label}</span>
                                            </span>
                                            <span className="text-foreground font-medium">
                                                {entry.count} ({entry.ratio}%)
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </AnalyticsChartCard>
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                    <AnalyticsChartCard
                        className="xl:col-span-3"
                        contentClassName="space-y-2"
                        docKey="homepage.top_sellers"
                        title={t('homepage.analytics.top_sellers.title', 'Top 5 sellers')}
                        description={t('homepage.analytics.by_total_revenue', 'By total revenue')}
                    >
                        <div className="text-muted-foreground grid grid-cols-[auto_1fr_auto] text-xs font-semibold uppercase">
                            <span>#</span>
                            <span>{t('homepage.analytics.table.employee', 'Employee')}</span>
                            <span>{t('homepage.analytics.table.revenue', 'Revenue')}</span>
                        </div>
                        {topSalesmen.length === 0 && <EmptyState title={t('homepage.analytics.empty.no_sellers', 'No sellers in selected range')} />}
                        {topSalesmen.map((salesman, index) => (
                            <div key={salesman.id} className="border-border grid grid-cols-[auto_1fr_auto] items-center gap-3 border-t pt-2">
                                <span className="bg-muted text-muted-foreground flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold">
                                    {index + 1}
                                </span>
                                <div className="flex min-w-0 items-center gap-2">
                                    <AvatarOrInitials name={salesman.name} avatar={salesman.avatar} />
                                    <Link href={`/employees/${salesman.id}`} className="text-foreground truncate text-sm font-medium hover:underline">
                                        {salesman.name}
                                    </Link>
                                </div>
                                <span className="text-foreground text-right text-sm font-semibold">{formatCurrency(salesman.revenue)}</span>
                            </div>
                        ))}
                    </AnalyticsChartCard>

                    <AnalyticsChartCard
                        className="xl:col-span-3"
                        contentClassName="space-y-2"
                        docKey="homepage.top_service_employees"
                        title={t('homepage.analytics.top_service_employees.title', 'Top 5 service employees')}
                        description={t('homepage.analytics.by_completed_work_orders', 'By completed work orders')}
                    >
                        <div className="text-muted-foreground grid grid-cols-[auto_1fr_auto] text-xs font-semibold uppercase">
                            <span>#</span>
                            <span>{t('homepage.analytics.table.employee', 'Employee')}</span>
                            <span>{t('homepage.analytics.table.completed', 'Completed')}</span>
                        </div>
                        {topServiceEmployees.length === 0 && (
                            <EmptyState
                                title={t('homepage.analytics.empty.no_service_employee_data', 'No service employee data in selected range')}
                            />
                        )}
                        {topServiceEmployees.map((employee, index) => (
                            <div key={employee.id} className="border-border grid grid-cols-[auto_1fr_auto] items-center gap-3 border-t pt-2">
                                <span className="bg-muted text-muted-foreground flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold">
                                    {index + 1}
                                </span>
                                <div className="flex min-w-0 items-center gap-2">
                                    <AvatarOrInitials name={employee.name} avatar={employee.avatar} />
                                    <Link href={`/employees/${employee.id}`} className="text-foreground truncate text-sm font-medium hover:underline">
                                        {employee.name}
                                    </Link>
                                </div>
                                <span className="text-foreground text-right text-sm font-semibold">{employee.completedCount}</span>
                            </div>
                        ))}
                    </AnalyticsChartCard>

                    <AnalyticsChartCard
                        className="xl:col-span-3"
                        contentClassName="space-y-2"
                        docKey="homepage.top_companies"
                        title={t('homepage.analytics.top_companies.title', 'Top 5 companies')}
                        description={t('homepage.analytics.by_total_revenue', 'By total revenue')}
                    >
                        <div className="text-muted-foreground grid grid-cols-[auto_1fr_auto_auto] gap-2 text-xs font-semibold uppercase">
                            <span>#</span>
                            <span>{t('homepage.analytics.table.company', 'Company')}</span>
                            <span className="text-right">{t('homepage.analytics.table.revenue', 'Revenue')}</span>
                            <span className="text-right">{t('homepage.analytics.table.projects', 'Projects')}</span>
                        </div>
                        {topCompanies.length === 0 && (
                            <EmptyState title={t('homepage.analytics.empty.no_company_data', 'No company data in selected range')} />
                        )}
                        {topCompanies.map((company, index) => (
                            <div key={company.id} className="border-border grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 border-t pt-2">
                                <span className="bg-muted text-muted-foreground flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold">
                                    {index + 1}
                                </span>
                                <Link href={`/companies/${company.id}`} className="text-foreground truncate text-sm font-medium hover:underline">
                                    {company.name}
                                </Link>
                                <span className="text-foreground text-right text-sm font-semibold">{formatCurrency(company.revenue)}</span>
                                <span className="text-muted-foreground text-right text-sm">{company.projectsCount}</span>
                            </div>
                        ))}
                    </AnalyticsChartCard>

                    <AnalyticsChartCard
                        className="xl:col-span-3"
                        contentClassName="space-y-2"
                        docKey="homepage.insights"
                        icon={<Lightbulb className="text-muted-foreground h-4 w-4" />}
                        title={t('homepage.analytics.insights.title', 'Insights and warnings')}
                    >
                        {insights.map((item, index) => (
                            <div
                                key={`insight-${index}`}
                                className="hover:bg-muted/50 flex items-center justify-between rounded-lg border p-3 transition-colors"
                            >
                                <div className="flex items-start gap-2.5">
                                    <span className="bg-muted mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
                                        <item.icon className={`h-4 w-4 ${item.iconColor}`} />
                                    </span>
                                    <div>
                                        <p className="text-foreground text-sm font-semibold">{toSentenceCase(item.title)}</p>
                                        <p className="text-muted-foreground text-xs">{toSentenceCase(item.text)}</p>
                                    </div>
                                </div>
                                <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
                            </div>
                        ))}
                    </AnalyticsChartCard>
                </div>
            </div>

            {isNavigating && (
                <div className="absolute inset-0 flex items-start justify-center pt-24" aria-live="polite">
                    <div className="bg-background flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('analytics.crm_dashboard.loading', 'Updating…')}
                    </div>
                </div>
            )}
        </div>
    );
}
